import { getPluralizationLabel } from "@components/smart/FieldInfo";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy } from "@components/smart/smartTable";
import { PrValueTypeCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { CommonReportProps } from "@pages/reports/CommonDefs";
import {
    composedDateRangeOnBeforeLoadCallback,
    getComposedDateRangeFieldsDefs
} from "@pages/reports/customFilterComponents/ComposedDateRange";
import {
    getEmployeeNumberOursCommonDef,
    getPayslipCommonDef,
    payslipInitialSortBy
} from "@pages/reports/payrollOverview/PayrollOverview.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { ReportStorage } from "@pages/reports/ReportStorage";
import { isDefined } from "@utils/general";
import i18next from "i18next";

import { TValue } from "../../../global.types";
import { IReportTableDefinition, TReportColumnOverrides } from "../Report.utils";
import { ReportId } from "../ReportIds";

function getUnitForPrValueTypeCode(valueTypeCode: PrValueTypeCode, count?: number): string {
    function translate(key: string): string {
        return getPluralizationLabel(key, count);
    }

    switch (valueTypeCode) {
        case PrValueTypeCode.Days:
            return translate("Common:Time.Days");
        case PrValueTypeCode.Months:
            return translate("Common:Time.Month");
        case PrValueTypeCode.Minutes:
            return translate("Common:Time.Minutes");
        case PrValueTypeCode.Hours:
            return translate("Common:Time.Hours");
        case PrValueTypeCode.Percentage:
            return "%";
        case PrValueTypeCode.Count:
            return translate("Common:General.PiecesUnit");
        case PrValueTypeCode.Amount:
        case PrValueTypeCode.AmountPerHour:
        case PrValueTypeCode.Code:
        case PrValueTypeCode.Date:
            // these types should not be used for PrPayslipEmploymentItem_IntCount, but if they are, we return an empty string
            return "";
    }
}

export const getDefinition = (): IReportTableDefinition => {
    const tableId = ReportId.BenefitOverview;
    const title = i18next.t("Reporting:BenefitOverview.Title");
    const path = "BenefitsOverview";
    
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
        defaultFilters: [
            CommonReportProps.dateRange,
            CommonReportProps.dateRangeCustomValue,
        ],
        filterDefinition: {
            ...getComposedDateRangeFieldsDefs(tableId, true),
        },
    }, {
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        allowCustomFilters: false,
        defaultFilters: [],
        filterDefinition: {}
    }];


    const onBeforeLoad = async (storage: ReportStorage) => {
        await composedDateRangeOnBeforeLoadCallback(storage, false, true, true);
    };

    const parameters: string[] = [CommonReportProps.dateRange, CommonReportProps.dateRangeCustomValue];

    const defaultReportHierarchy: IReportHierarchy = {
        "Aggregate": false,
        "Groups": [],
        "Columns": [
            {
                "ColumnAlias": "YearMonth",
            },
            {
                "ColumnAlias": "PrEmployee_NumberOurs",
            },
            {
                "ColumnAlias": "Name",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_TypeName",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_IntCount",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_Value",
            }
        ],
        "Aggregations": []
    };


    const columnOverrides: TReportColumnOverrides = {
        YearMonth: getPayslipCommonDef(),
        PrEmployee_NumberOurs: getEmployeeNumberOursCommonDef(),
        PrPayslipEmploymentItem_IntCount: {
            formatter: (val: TValue, args: IFormatOptions) => {
                if (!isDefined(val) || isNaN(Number(val))) {
                    return null;
                }
                const unit = getUnitForPrValueTypeCode(args.entity["PrPayslipEmploymentItem_IntCountUnit"], Number(val)) ?? "";
                return `${val} ${unit}`;
            }
        }
    };

    const initialSortBy = payslipInitialSortBy;


    return {
        title,
        path,
        id: tableId,
        initialSortBy,
        columnOverrides,
        filterBarDef,
        onBeforeLoad,
        parameters,
        defaultReportHierarchy
    };
};

setReportDef(ReportId.BenefitOverview, getDefinition);